import { type ReactNode, useEffect, useMemo, useState } from 'react';

import {
  AD_DETAIL_WINDOWS,
  type AdDetail as AdDetailData,
  type AdDetailWindowName,
  type AdsMetricKey,
  type AdsMetricValues,
  ApiError,
  fetchAdDetail,
} from '../api.ts';
import { formatMoney } from '../adsMoney.ts';
import { type AssetRef, useAssetDeliveries } from '../assets.ts';
import { describeError } from '../data.tsx';
import { DecisionList } from '../DecisionLog.tsx';
import { Icon } from '../Icon.tsx';
import { adMetaIdFromPath, adsDecisionsPath, Link, useLocation } from '../router.tsx';
import { EmptyState, Notice, PageHeader, Panel, Skeleton } from '../ui.tsx';
import { adsManagerUrl, age, ObjectStatus, percent } from './Ads.tsx';

/**
 * One ad in full (meta-ads-agent US-022): its status and the rule milestones,
 * the creative and copy it runs, every number per window against the other
 * ads of its ad set, its days, and every decision about it. All of it is
 * `GET /api/ads/ads/:metaId`, a read of what is stored; the "× other ads"
 * ratios are the server's (`ads/metrics.ts`), the ones the agent's snapshot
 * carries, so the page never works out a number of its own.
 */

const POLL_MS = 60_000;

const ATTRIBUTION_TITLE =
  'Meta still attributes conversions to the last 48 hours; these counts can still go up.';

const WINDOW_LABEL: Readonly<Record<AdDetailWindowName, string>> = {
  last_7d: 'Last 7 days',
  previous_7d: 'Previous 7 days',
  last_30d: 'Last 30 days',
  lifetime: 'Lifetime',
};

type Format = 'money' | 'count' | 'percent' | 'decimal';

interface MetricRow {
  readonly key: AdsMetricKey;
  readonly label: string;
  readonly format: Format;
  readonly video?: boolean;
}

export function AdDetail() {
  const { pathname } = useLocation();
  const metaId = adMetaIdFromPath(pathname) ?? '';
  const [detail, setDetail] = useState<AdDetailData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    const load = (): void => {
      fetchAdDetail(metaId, controller.signal)
        .then((next) => {
          setDetail(next);
          setError(null);
          setMissing(false);
        })
        .catch((cause: unknown) => {
          if (controller.signal.aborted) return;
          if (cause instanceof ApiError && cause.status === 404) setMissing(true);
          else setError(describeError(cause));
        });
    };
    load();
    const timer = window.setInterval(load, POLL_MS);
    return () => {
      controller.abort();
      window.clearInterval(timer);
    };
  }, [metaId]);

  const back = { href: '/ads', label: 'Ads' } as const;

  if (missing) {
    return (
      <div className="page">
        <PageHeader title="Ad" back={back} />
        <EmptyState icon="graph" title="No such ad">
          <code>{metaId}</code> is not an ad the agent made.
        </EmptyState>
      </div>
    );
  }

  if (detail?.ad.metaId !== metaId) {
    return (
      <div className="page">
        <PageHeader title="Ad" back={back} />
        {error === null ? <Skeleton lines={6} /> : <Notice kind="error">{error}</Notice>}
      </div>
    );
  }

  return (
    <div className="page">
      <AdHeader detail={detail} />
      {error !== null && <Notice kind="error">{error}</Notice>}
      <ReviewNotice detail={detail} />
      <div className="ad-detail__panels">
        <CreativePanel detail={detail} />
        <CopyPanel detail={detail} />
      </div>
      <MetricsPanel detail={detail} />
      <DailyPanel detail={detail} />
      <Panel
        title="Decisions"
        icon="history"
        meta={<span className="muted">{detail.decisions.length}</span>}
        actions={
          <Link className="link" href={adsDecisionsPath(detail.ad.metaId)}>
            Open in the decision log
          </Link>
        }
      >
        <DecisionList decisions={detail.decisions} empty="No decision about this ad yet." />
      </Panel>
    </div>
  );
}

/* ---------------------------------------------------------------- header */

function AdHeader({ detail }: { readonly detail: AdDetailData }) {
  const { ad, milestones } = detail;
  const money = (minor: number): string => formatMoney(minor, detail.currency);
  const eyebrow = [detail.campaign?.name, detail.adSet?.name].filter((name): name is string => name !== undefined);

  let fairTest: string | null = null;
  if (milestones.fairTestOn !== null) fairTest = `Fair test reached on ${day(milestones.fairTestOn)}`;
  else if (milestones.ageReachedOn !== null && milestones.testSpendMinor !== null)
    fairTest = `Fair test once it has run to ${day(milestones.ageReachedOn)} and spent ${money(milestones.testSpendMinor)} (${money(milestones.lifetimeSpendMinor)} so far)`;

  return (
    <>
      <PageHeader
        back={{ href: '/ads', label: 'Ads' }}
        {...(eyebrow.length === 0 ? {} : { eyebrow: eyebrow.join(' › ') })}
        title={<span className="ad-detail__title">{ad.name}</span>}
        subtitle={
          <span className="ads-status">
            <ObjectStatus node={ad} />
            <span className="ads-status__text">
              {ad.liveSince === null
                ? 'Not live yet'
                : `Live ${age(ad.ageHours)}, since ${day(ad.liveSince)}`}
            </span>
          </span>
        }
        actions={
          <a
            className="button button--quiet"
            href={adsManagerUrl(detail.adAccountId, { kind: 'ad', metaId: ad.metaId })}
            target="_blank"
            rel="noreferrer"
          >
            Ads Manager <Icon name="link-external" />
          </a>
        }
      />
      {(fairTest !== null || milestones.retirementCheckOn !== null) && (
        <ul className="ad-detail__milestones">
          {fairTest !== null && (
            <li className={milestones.fairTestOn === null ? undefined : 'ad-detail__milestone--reached'}>
              <Icon name={milestones.fairTestOn === null ? 'clock' : 'check-circle'} />
              {fairTest}
            </li>
          )}
          {milestones.retirementCheckOn !== null && (
            <li>
              <Icon name="calendar" />
              Retirement check on {day(milestones.retirementCheckOn)}
            </li>
          )}
        </ul>
      )}
    </>
  );
}

/** Meta's verdict when it rejected the ad, is still reviewing it, or reports a delivery issue. */
function ReviewNotice({ detail }: { readonly detail: AdDetailData }) {
  const status = detail.ad.effectiveStatus ?? detail.ad.status;
  const feedback = detail.ad.reviewFeedback;
  const lines =
    feedback.length === 0 ? null : (
      <ul className="ad-detail__feedback">
        {feedback.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
    );
  if (status === 'DISAPPROVED')
    return (
      <Notice kind="error">
        <strong>Meta rejected this ad.</strong>
        {lines ?? ' Meta gave no reason.'}
      </Notice>
    );
  if (status === 'PENDING_REVIEW' || status === 'IN_PROCESS')
    return (
      <Notice kind="wait">
        <strong>Meta is reviewing this ad.</strong> It does not run until the review passes.
        {lines}
      </Notice>
    );
  if (lines !== null)
    return (
      <Notice kind="warn">
        <strong>Meta reports a problem with this ad.</strong>
        {lines}
      </Notice>
    );
  return null;
}

/* -------------------------------------------------------- creative, copy */

function CreativePanel({ detail }: { readonly detail: AdDetailData }) {
  const { creative } = detail;
  const refs = useMemo((): AssetRef[] => {
    if (creative === null) return [];
    return [creative.render?.assetId, creative.poster?.assetId]
      .filter((id): id is string => id !== null && id !== undefined)
      .map((assetId) => ({ contentId: creative.contentId, assetId }));
  }, [creative]);
  const urls = useAssetDeliveries(refs);

  if (creative === null) {
    return (
      <Panel title="Creative" icon="image">
        <p className="muted">The content this ad was made from is no longer in marketingchief.</p>
      </Panel>
    );
  }
  const url = (assetId: string | null | undefined): string | undefined =>
    assetId === null || assetId === undefined ? undefined : urls.get(assetId)?.url;
  const renderUrl = url(creative.render?.assetId);
  const posterUrl = url(creative.poster?.assetId);

  let media: ReactNode;
  if (creative.kind === 'video' && renderUrl !== undefined) {
    media = (
      <video className="ad-detail__media" controls playsInline preload="metadata" poster={posterUrl}>
        <source src={renderUrl} type={creative.render?.mime} />
      </video>
    );
  } else if (renderUrl !== undefined || posterUrl !== undefined) {
    media = <img className="ad-detail__media" src={renderUrl ?? posterUrl} alt={`Creative of ${detail.ad.name}`} />;
  } else {
    media = <p className="muted">The file of this version is not in the bucket (yet), so there is nothing to show.</p>;
  }

  return (
    <Panel
      title="Creative"
      icon={creative.kind === 'video' ? 'device-camera-video' : 'image'}
      actions={
        <Link className="link" href={`/content/${encodeURIComponent(creative.contentId)}`}>
          {creative.contentName} · v{creative.versionNumber}
        </Link>
      }
    >
      <div className="ad-detail__creative">{media}</div>
    </Panel>
  );
}

function CopyPanel({ detail }: { readonly detail: AdDetailData }) {
  const { copy } = detail;
  if (copy === null) {
    return (
      <Panel title="Copy" icon="quote">
        <p className="muted">No copy was stored for this ad.</p>
      </Panel>
    );
  }
  const group = (title: string, texts: readonly string[]): ReactNode => (
    <div className="ad-detail__copy-group">
      <h3 className="ads-kpi__heading">
        {title} <span className="muted">({texts.length})</span>
      </h3>
      {texts.length === 0 ? (
        <p className="muted">None.</p>
      ) : (
        <ol className="ad-detail__texts">
          {texts.map((text, index) => (
            <li key={`${String(index)}-${text}`}>{text}</li>
          ))}
        </ol>
      )}
    </div>
  );
  return (
    <Panel title="Copy" icon="quote" meta={copy.concept === '' ? undefined : <span className="muted">{copy.concept}</span>}>
      {group('Primary texts', copy.primaryTexts)}
      {group('Headlines', copy.headlines)}
      {group('Descriptions', copy.descriptions)}
      <dl className="facts">
        <div className="facts__row">
          <dt>Call to action</dt>
          <dd>{copy.cta === '' ? '—' : sentence(copy.cta)}</dd>
        </div>
        <div className="facts__row">
          <dt>Landing page</dt>
          <dd className="ad-detail__url">
            {copy.url === '' ? (
              '—'
            ) : (
              <a className="link" href={copy.url} target="_blank" rel="noreferrer">
                {copy.url}
              </a>
            )}
          </dd>
        </div>
      </dl>
    </Panel>
  );
}

/* --------------------------------------------------------------- metrics */

function MetricsPanel({ detail }: { readonly detail: AdDetailData }) {
  const video = detail.creative?.kind === 'video';
  const proxy = detail.proxyEventLabel ?? 'proxy event';
  const rows: readonly MetricRow[] = [
    { key: 'spendMinor', label: 'Spend', format: 'money' },
    { key: 'impressions', label: 'Impressions', format: 'count' },
    { key: 'reach', label: 'Reach', format: 'count' },
    { key: 'frequency', label: 'Frequency', format: 'decimal' },
    { key: 'cpmMinor', label: 'CPM', format: 'money' },
    { key: 'linkClicks', label: 'Link clicks', format: 'count' },
    { key: 'linkCtr', label: 'Link CTR', format: 'percent' },
    { key: 'costPerLinkClickMinor', label: 'Cost per link click', format: 'money' },
    { key: 'landingPageViews', label: 'Landing-page views', format: 'count' },
    { key: 'costPerLandingPageViewMinor', label: 'Cost per landing-page view', format: 'money' },
    { key: 'proxyEvents', label: `Proxy events (${proxy})`, format: 'count' },
    { key: 'costPerProxyEventMinor', label: 'Cost per proxy event', format: 'money' },
    { key: 'bookedCalls', label: `Booked calls (${detail.bookedCallLabel})`, format: 'count' },
    { key: 'costPerBookedCallMinor', label: 'Cost per booked call', format: 'money' },
    { key: 'video3sViews', label: '3-second views', format: 'count', video: true },
    { key: 'hookRate', label: 'Hook rate', format: 'percent', video: true },
    { key: 'thruplays', label: 'ThruPlays', format: 'count', video: true },
  ];
  const format = (value: number | null, kind: Format): string => {
    if (value === null) return '—';
    if (kind === 'money') return formatMoney(value, detail.currency);
    if (kind === 'percent') return percent(value);
    if (kind === 'decimal') return value.toFixed(2);
    return value.toLocaleString('en');
  };
  const compared = detail.otherAdCount > 0;
  return (
    <Panel
      title="Metrics"
      icon="pulse"
      meta={
        <span className="muted">
          {compared
            ? `against the average of the ${detail.otherAdCount === 1 ? 'other ad' : `${String(detail.otherAdCount)} other ads`} in the ad set`
            : 'the only ad in its ad set: nothing to compare with'}
        </span>
      }
    >
      <div className="table-wrap">
        <table className="table ad-metrics">
          <thead>
            <tr>
              <th scope="col">Metric</th>
              {AD_DETAIL_WINDOWS.map((name) => (
                <th scope="col" className="num" key={name}>
                  {WINDOW_LABEL[name]}
                  {detail.metrics[name].incompleteAttribution && <Incomplete />}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows
              .filter((row) => row.video !== true || video)
              .map((row) => (
                <tr key={row.key}>
                  <th scope="row">{row.label}</th>
                  {AD_DETAIL_WINDOWS.map((name) => {
                    const window = detail.metrics[name];
                    return (
                      <td className="num" key={name}>
                        <span className="ad-metrics__value">{format(valueOf(window.values, row.key), row.format)}</span>
                        {window.vsOtherAds !== null && (
                          <span className="ad-metrics__vs">{times(window.vsOtherAds[row.key])}</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

function valueOf(values: AdsMetricValues, key: AdsMetricKey): number | null {
  return values[key];
}

/** `2.4` → "2.4× other ads"; `null` (nothing to divide by) → "— vs other ads". */
function times(ratio: number | null): string {
  if (ratio === null) return '— vs other ads';
  const digits = ratio >= 10 ? 0 : ratio < 0.1 ? 2 : 1;
  return `${ratio.toFixed(digits)}× other ads`;
}

function Incomplete() {
  return (
    <span className="ad-metrics__incomplete" title={ATTRIBUTION_TITLE}>
      attribution still coming in
    </span>
  );
}

/* ----------------------------------------------------------------- daily */

function DailyPanel({ detail }: { readonly detail: AdDetailData }) {
  const money = (minor: number): string => formatMoney(minor, detail.currency);
  return (
    <Panel title="Day by day" icon="calendar" meta={<span className="muted">last 30 days, newest first</span>}>
      {detail.daily.length === 0 ? (
        <p className="muted">No day with numbers yet. The sync stores them once the ad delivers.</p>
      ) : (
        <div className="table-wrap">
          <table className="table ad-daily">
            <thead>
              <tr>
                <th scope="col">Date</th>
                <th scope="col" className="num">Spend</th>
                <th scope="col" className="num">Impressions</th>
                <th scope="col" className="num">Link clicks</th>
                <th scope="col" className="num">Landing-page views</th>
                <th scope="col" className="num">Proxy events</th>
                <th scope="col" className="num">Booked calls</th>
              </tr>
            </thead>
            <tbody>
              {detail.daily.map((row) => (
                <tr key={row.date}>
                  <th scope="row" className="table__nowrap">
                    {day(row.date)}
                    {row.incompleteAttribution && <Incomplete />}
                  </th>
                  <td className="num">{money(row.spendMinor)}</td>
                  <td className="num">{row.impressions.toLocaleString('en')}</td>
                  <td className="num">{row.linkClicks.toLocaleString('en')}</td>
                  <td className="num">{row.landingPageViews.toLocaleString('en')}</td>
                  <td className="num">{row.proxyEvents.toLocaleString('en')}</td>
                  <td className="num">{row.bookedCalls.toLocaleString('en')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

/* --------------------------------------------------------------- helpers */

/** A stored `YYYY-MM-DD` (an account day) as "5 Oct 2026", without shifting it into the browser's zone. */
function day(date: string): string {
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(`${date}T00:00:00Z`),
  );
}

/** `BOOK_NOW` → "Book now". */
function sentence(value: string): string {
  const words = value.toLowerCase().replace(/_/gu, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}
